// =============================================================================
// MeuDriver — Hook: Trilha de aprendizado do estudante
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface LearningMilestone {
  id: string
  title: string
  description: string | null
  requirement_type: 'lessons_count' | 'quiz_score' | 'streak_days' | 'manual'
  requirement_value: number
  order_index: number
  badge_icon: string
}

export interface StudentMilestoneProgress {
  id: string
  student_id: string
  milestone_id: string
  achieved_at: string
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Lista todos os milestones em ordem */
export function useLearningMilestones() {
  return useQuery({
    queryKey: ['learning-milestones'],
    queryFn: async (): Promise<LearningMilestone[]> => {
      const { data, error } = await supabase
        .from('learning_milestones')
        .select('*')
        .order('order_index', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []) as LearningMilestone[]
    },
    staleTime: 1000 * 60 * 60, // 1h — milestone muda raramente
  })
}

/** Milestones que o estudante atual já alcançou */
export function useStudentMilestoneProgress() {
  const { user } = useAuth()
  return useQuery({
    queryKey: ['student-milestone-progress', user?.id],
    queryFn: async (): Promise<StudentMilestoneProgress[]> => {
      if (!user?.id) return []
      const { data, error } = await supabase
        .from('student_milestone_progress')
        .select('*')
        .eq('student_id', user.id)
      if (error) throw new Error(error.message)
      return (data ?? []) as StudentMilestoneProgress[]
    },
    enabled: Boolean(user?.id),
  })
}

// Tipo de retorno do RPC check_and_grant_milestones
interface MilestoneGrantResult {
  milestone_id: string
  milestone_name: string
  just_granted: boolean
}

/**
 * Verifica e concede milestones server-side via RPC SECURITY DEFINER.
 * A contagem de aulas concluídas é feita no banco — o frontend não passa
 * completedLessons como parâmetro (ALTO-002: previne self-grant fraudulento).
 */
export function useCheckAndGrantMilestones() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('check_and_grant_milestones')
      if (error) throw new Error(error.message)
      return (data ?? []) as MilestoneGrantResult[]
    },
    onSuccess: (results) => {
      const newlyGranted = results.filter((r) => r.just_granted)
      if (!newlyGranted.length) return
      queryClient.invalidateQueries({ queryKey: ['student-milestone-progress'] })
      newlyGranted.forEach((r) => {
        toast.success(`Conquista desbloqueada: ${r.milestone_name}`)
      })
    },
    onError: (err) => {
      console.error('[useLearningPath] checkAndGrant error:', err)
    },
  })
}
